import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Card,
  CircularProgress,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import { I_Installation } from 'src/interfaces/InstallationTypes';
import CancelIcon from '@mui/icons-material/Cancel';
import BuildIcon from '@mui/icons-material/Build';
import { FormattedMessage } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import routes from '../../../Resources/routes.json';
import { ProductIdContext } from '../../../contexts/ProductIdContextProvider';
import SearchTwoToneIcon from '@mui/icons-material/SearchTwoTone';

interface FlatInstallationViewProps {
  installations: I_Installation[];
}

const FlatInstallationView = (props: FlatInstallationViewProps) => {
  const [isRowHovered, setHoveredRow] = useState(-1);
  const navigate = useNavigate();
  const [selectedInstallation, setSelectedInstallation] = useState<number>(-1);
  const currentLocation = useLocation();
  const { product, setProduct } = useContext(ProductIdContext);
  const BATCH_SIZE = 50;
  const [visibleCount, setVisibleCount] = useState(BATCH_SIZE);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortByStatus, setSortByStatus] = useState('All Installations');
  const [sortByAction, setSortByAction] = useState('All Installations');

  const HoverableTableRow = styled(TableRow)(({ theme }) => ({
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover // Or any other color
    }
  }));

  const indexedData = useMemo(() => {
    return props.installations.map((item) => ({
      ...item,
      nameLower: item.name.toLowerCase(),
      locationLower: item.location.toLowerCase(),
      regionLower: item.region.toLowerCase()
    }));
  }, [props.installations]);

  const sortedInstallations = useMemo(() => {
    let filtered = indexedData.filter(
      (item) =>
        item.nameLower.includes(searchTerm.toLowerCase()) ||
        item.locationLower.includes(searchTerm.toLowerCase()) ||
        item.regionLower.includes(searchTerm.toLowerCase())
    );

    // Apply the 'showOnly' filter
    switch (sortByStatus) {
      case 'Installations With Alarm':
        filtered = filtered.filter((i) => i.status === 2);
        break;
      case 'Installations with Warning':
        filtered = filtered.filter((i) => i.status === 1);
        break;
      case 'Functional Installations':
        filtered = filtered.filter((i) => i.status === 0);
        break;
      case 'Offline Installations':
        filtered = filtered.filter((i) => i.status === -1);
        break;
    }

    switch (sortByAction) {
      case 'Installations With Action Flag':
        filtered = filtered.filter((i) => i.testingMode === true);
        break;
      case 'Installations Without Action Flag':
        filtered = filtered.filter((i) => i.testingMode === false);
        break;
    }

    // Sort by status (alarms first)
    return filtered.sort((a, b) => {
      const a_status = a.status;
      const b_status = b.status;

      if (a_status > b_status) return -1;
      if (a_status < b_status) return 1;
      return 0;
    });
  }, [searchTerm, indexedData, sortByAction, sortByStatus]);

  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisibleCount((prev) => prev + BATCH_SIZE);
        }
      },
      {
        rootMargin: '600px', // triggers before the element fully enters view
        threshold: 0.5 // triggers when 10% of it is visible
      }
    );

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const handleSelectOneInstallation = (installationID: number): void => {
    if (selectedInstallation != installationID) {
      setSelectedInstallation(installationID);
      setSelectedInstallation(-1);

      navigate(
        product === 0
          ? routes.installations +
              routes.list +
              routes.installation +
              `${installationID}` +
              '/' +
              routes.live
          : routes.sodistore_installations +
              routes.list +
              routes.installation +
              `${installationID}` +
              '/' +
              routes.live,
        {
          replace: true
        }
      );
    } else {
      setSelectedInstallation(-1);
    }
  };

  const theme = useTheme();

  const handleRowMouseEnter = (id: number) => {
    setHoveredRow(id);
  };

  const handleRowMouseLeave = () => {
    setHoveredRow(-1);
  };

  return (
    <>
      <Grid container spacing={1}>
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display:
              currentLocation.pathname === routes.installations + 'list' ||
              currentLocation.pathname === routes.installations + routes.list ||
              currentLocation.pathname ===
                routes.sodistore_installations + 'list' ||
              currentLocation.pathname ===
                routes.sodistore_installations + routes.list
                ? 'block'
                : 'none'
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '16px', // spacing between elements
              width: '100%'
            }}
          >
            <FormControl sx={{ flex: 1 }}>
              <TextField
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchTwoToneIcon />
                    </InputAdornment>
                  )
                }}
              />
            </FormControl>

            <FormControl sx={{ width: 240 }}>
              <InputLabel>
                <FormattedMessage
                  id="sortByStatus"
                  defaultMessage="Sort By Status"
                />
              </InputLabel>
              <Select
                value={sortByStatus}
                onChange={(e) => setSortByStatus(e.target.value)}
                label="Show Only"
              >
                {[
                  'All Installations',
                  'Installations With Alarm',
                  'Installations with Warning',
                  'Functional Installations',
                  'Offline Installations'
                ].map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ width: 240 }}>
              <InputLabel>
                <FormattedMessage
                  id="sortByActionFlag"
                  defaultMessage="Sort By Action Flag"
                />
              </InputLabel>
              <Select
                value={sortByAction}
                onChange={(e) => setSortByAction(e.target.value)}
                label="Show Only"
              >
                {[
                  'All Installations',
                  'Installations With Action Flag',
                  'Installations Without Action Flag'
                ].map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </Grid>
      </Grid>

      <Grid container spacing={1} sx={{ marginTop: 0.1 }}>
        <Grid
          item
          sx={{
            display:
              currentLocation.pathname === routes.installations + 'list' ||
              currentLocation.pathname === routes.installations + routes.list ||
              currentLocation.pathname ===
                routes.sodistore_installations + 'list' ||
              currentLocation.pathname ===
                routes.sodistore_installations + routes.list
                ? 'block'
                : 'none'
          }}
        >
          <Card>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <FormattedMessage id="name" defaultMessage="Name" />
                    </TableCell>
                    <TableCell>
                      <FormattedMessage
                        id="location"
                        defaultMessage="Location"
                      />
                    </TableCell>
                    <TableCell>
                      <FormattedMessage id="country" defaultMessage="Country" />
                    </TableCell>
                    <TableCell>
                      <FormattedMessage
                        id="orderNumbers"
                        defaultMessage="Order Numbers"
                      />
                    </TableCell>
                    <TableCell>
                      <FormattedMessage id="status" defaultMessage="Status" />
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedInstallations.map((installation) => {
                    const isInstallationSelected =
                      installation.id === selectedInstallation;

                    const status = installation.status;
                    const rowStyles =
                      isRowHovered === installation.id
                        ? {
                            cursor: 'pointer',
                            backgroundColor: theme.colors.primary.lighter
                          }
                        : {};

                    return (
                      <TableRow
                        hover
                        key={installation.id}
                        selected={isInstallationSelected}
                        style={rowStyles}
                        onClick={() =>
                          handleSelectOneInstallation(installation.id)
                        }
                        onMouseEnter={() =>
                          handleRowMouseEnter(installation.id)
                        }
                        onMouseLeave={() => handleRowMouseLeave()}
                      >
                        <TableCell>
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            color="text.primary"
                            gutterBottom
                            noWrap
                            sx={{ marginTop: '10px', fontSize: 'small' }}
                          >
                            {installation.name}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            color="text.primary"
                            gutterBottom
                            noWrap
                            sx={{ marginTop: '10px', fontSize: 'small' }}
                          >
                            {installation.location}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            color="text.primary"
                            gutterBottom
                            noWrap
                            sx={{ marginTop: '10px', fontSize: 'small' }}
                          >
                            {installation.country}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            color="text.primary"
                            gutterBottom
                            noWrap
                            sx={{ marginTop: '10px', fontSize: 'small' }}
                          >
                            {installation.orderNumbers}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              marginLeft: '15px'
                            }}
                          >
                            {status === -1 ? (
                              <CancelIcon
                                style={{
                                  width: '23px',
                                  height: '23px',
                                  color: 'red',
                                  borderRadius: '50%'
                                }}
                              />
                            ) : (
                              ''
                            )}

                            {status === -2 ? (
                              <CircularProgress
                                size={20}
                                sx={{
                                  color: '#f7b34d'
                                }}
                              />
                            ) : (
                              ''
                            )}

                            <div
                              style={{
                                width: '20px',
                                height: '20px',
                                marginLeft: '2px',
                                borderRadius: '50%',
                                backgroundColor:
                                  status === 2
                                    ? 'red'
                                    : status === 1
                                    ? 'orange'
                                    : status === -1 || status === -2
                                    ? 'transparent'
                                    : 'green'
                              }}
                            />

                            {installation.testingMode && (
                              <BuildIcon
                                style={{
                                  width: '23px',
                                  height: '23px',
                                  color: 'purple',
                                  borderRadius: '50%',
                                  position: 'relative',
                                  zIndex: 1,
                                  marginLeft: '15px'
                                }}
                              />
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>
      </Grid>
    </>
  );
};

export default FlatInstallationView;
